import * as Y from 'yjs'
import type { Awareness } from 'y-protocols/awareness'
import { createDiagramDoc, getNodesMap, getEdgesMap } from './yjsDocument'

/**
 * Sync provider abstraction over a Yjs document.
 *
 * A SyncProvider owns (or wraps) a Y.Doc and knows how to connect/disconnect it
 * to whatever transport backs collaboration. The default LocalSyncProvider has
 * no network and is the single-user provider; YWebsocketSyncProvider layers a
 * y-websocket transport on top for real-time multi-peer editing.
 *
 * These are ADDITIVE substrate helpers and are intentionally NOT wired into the
 * live editor render path. Runtime integration will consume them later.
 */
export interface SyncProvider {
  /** The shared document this provider syncs. */
  readonly doc: Y.Doc
  /** Presence/awareness channel, when the transport provides one. */
  readonly awareness?: Awareness
  /** Begin syncing (open the transport / mark the local provider active). */
  connect(): void
  /** Stop syncing (close the transport / mark the local provider inactive). */
  disconnect(): void
  /** Whether the provider is currently connected. */
  readonly connected: boolean
  /**
   * Register a callback fired once the initial state has synced from the
   * transport (or immediately, on a microtask, if already synced / network-free).
   * Lets callers read the doc AFTER the relay's existing content has arrived,
   * avoiding the seed-vs-sync race on an empty local doc.
   */
  onSync(cb: (synced: boolean) => void): void
}

/**
 * The default, network-free provider. Wraps a Y.Doc and toggles an internal
 * connected flag. Use this for single-user editing and as the substrate default
 * everywhere a SyncProvider is expected but no collaboration backend is wired.
 */
export class LocalSyncProvider implements SyncProvider {
  readonly doc: Y.Doc
  private _connected = false

  constructor(doc: Y.Doc = createDiagramDoc()) {
    this.doc = doc
  }

  get connected(): boolean {
    return this._connected
  }

  connect(): void {
    this._connected = true
  }

  disconnect(): void {
    this._connected = false
  }

  onSync(cb: (synced: boolean) => void): void {
    // No network: the local doc is the whole truth → "synced" immediately.
    queueMicrotask(() => cb(true))
  }
}

export interface YWebsocketSyncProviderOptions {
  /** WebSocket server URL, e.g. "wss://demos.yjs.dev". */
  url: string
  /** Room/document name to join. */
  room: string
  /** Existing doc to sync; a fresh diagram doc is created when omitted. */
  doc?: Y.Doc
  /**
   * Also persist the doc to IndexedDB (RTD-2) so the diagram loads instantly on
   * reconnect and survives offline. Lazily imports y-indexeddb in connect().
   */
  offline?: boolean
}

/**
 * A SyncProvider backed by y-websocket. The y-websocket import is performed
 * LAZILY inside connect() so this module stays tree-shakeable and never breaks
 * the SSR/library build or the (network-free) tests.
 */
interface WebsocketProviderLike {
  connect(): void
  disconnect(): void
  destroy(): void
  awareness: Awareness
  on(event: string, cb: (...args: any[]) => void): void
  synced: boolean
}

interface IndexeddbPersistenceLike {
  destroy(): void
}

class YWebsocketSyncProvider implements SyncProvider {
  readonly doc: Y.Doc
  private readonly url: string
  private readonly room: string
  private readonly offline: boolean
  // The concrete WebsocketProvider instance, created asynchronously after the
  // lazy y-websocket import resolves in connect().
  private provider: WebsocketProviderLike | null = null
  // The IndexedDB persistence (RTD-2), created lazily when offline is enabled.
  private persistence: IndexeddbPersistenceLike | null = null
  // True between connect() and disconnect(), independent of the async import.
  private active = false
  // Sync-state tracking for onSync() (the provider is created async, so callers
  // may register before the websocket exists).
  private _synced = false
  private _syncCbs: Array<(synced: boolean) => void> = []

  constructor(options: YWebsocketSyncProviderOptions) {
    this.doc = options.doc ?? createDiagramDoc()
    this.url = options.url
    this.room = options.room
    this.offline = options.offline ?? false
  }

  get awareness(): Awareness | undefined {
    return this.provider?.awareness
  }

  get connected(): boolean {
    return this.active
  }

  connect(): void {
    if (this.active) {
      this.provider?.connect()
      return
    }
    this.active = true
    // Lazy dynamic import so y-websocket is never pulled into builds/tests that
    // don't call connect(); this keeps the module tree-shakeable and SSR-safe.
    void import('y-websocket').then(({ WebsocketProvider }) => {
      // Guard against a disconnect() that raced ahead of the import.
      if (!this.active) return
      this.provider = new WebsocketProvider(
        this.url,
        this.room,
        this.doc,
      ) as unknown as WebsocketProviderLike
      // Fan the y-websocket 'sync' event out to onSync() subscribers.
      this.provider.on('sync', (synced: boolean) => {
        this._synced = synced
        if (synced) {
          const cbs = this._syncCbs
          this._syncCbs = []
          cbs.forEach((cb) => cb(true))
        }
      })
    })
    // RTD-2: layer IndexedDB persistence (also lazily imported) so the room is
    // cached locally for instant reload / offline. Best-effort: any failure
    // (e.g. no IndexedDB in the environment) leaves websocket sync untouched.
    if (this.offline) {
      void import('y-indexeddb')
        .then(({ IndexeddbPersistence }) => {
          if (!this.active) return
          this.persistence = new IndexeddbPersistence(
            this.room,
            this.doc,
          ) as unknown as IndexeddbPersistenceLike
        })
        .catch(() => {
          /* IndexedDB unavailable — sync still works over websocket */
        })
    }
  }

  onSync(cb: (synced: boolean) => void): void {
    if (this._synced) { queueMicrotask(() => cb(true)); return }
    this._syncCbs.push(cb)
  }

  disconnect(): void {
    this.active = false
    this._syncCbs = []
    this.provider?.disconnect()
    this.provider?.destroy()
    this.provider = null
    this.persistence?.destroy()
    this.persistence = null
  }
}

/**
 * Factory for a y-websocket backed SyncProvider. The provider stays disconnected
 * (and imports nothing) until connect() is called.
 */
export function createYWebsocketSyncProvider(
  options: YWebsocketSyncProviderOptions,
): SyncProvider {
  return new YWebsocketSyncProvider(options)
}

/**
 * Declarative SyncProvider config (RTD-5). `kind: 'local'` is the network-free
 * single-user provider; `kind: 'websocket'` joins a room (optionally with
 * IndexedDB offline caching). This is the single entry point callers should use
 * so the transport choice lives in one place and stays swappable (e.g. a future
 * managed Liveblocks adapter registers here).
 */
export type SyncProviderConfig =
  | { kind: 'local'; doc?: Y.Doc }
  | { kind: 'websocket'; url: string; room: string; doc?: Y.Doc; offline?: boolean }

export function createSyncProvider(config: SyncProviderConfig): SyncProvider {
  if (config.kind === 'local') {
    return new LocalSyncProvider(config.doc)
  }
  return new YWebsocketSyncProvider({
    url: config.url,
    room: config.room,
    doc: config.doc,
    offline: config.offline,
  })
}

export interface CollabUndoManagerOptions {
  /**
   * Transaction origins this manager captures. Changes made under other origins
   * are ignored, so e.g. agent-origin and user-origin edits can be undone
   * independently by separate managers.
   */
  trackedOrigins: unknown[]
}

/**
 * Build a Y.UndoManager scoped to the diagram's nodes + edges maps, capturing
 * only the supplied transaction origins.
 */
export function createCollabUndoManager(
  doc: Y.Doc,
  options: CollabUndoManagerOptions,
): Y.UndoManager {
  return new Y.UndoManager([getNodesMap(doc), getEdgesMap(doc)], {
    trackedOrigins: new Set(options.trackedOrigins),
  })
}
